import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  CircularProgress,
  Box,
  Typography,
  TextField,
  Button,
  Paper,
  Divider
} from '@mui/material';
import { Close, AutoAwesome, ArrowBack, Check } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import type { I18nPersonasNs } from '@/i18n/dictionaries/interfaces';
import { I18nNamespace } from '@/i18n/types/i18nNamespace';
import { AIRewriteStep } from './useAIRewrite';

interface AIRewriteDialogProps {
  isOpen: boolean;
  currentStep: AIRewriteStep;
  originalDescription: string;
  customInstructions: string;
  rewrittenDescription: string | null;
  isLoading: boolean;
  error: string | null;
  onInstructionsChange: (instructions: string) => void;
  onSubmit: () => void;
  onConfirm: () => void;
  onBack: () => void;
  onClose: () => void;
  onClearError: () => void;
}

const AIRewriteDialog: React.FC<AIRewriteDialogProps> = ({
  isOpen,
  currentStep,
  originalDescription,
  customInstructions,
  rewrittenDescription,
  isLoading,
  error,
  onInstructionsChange,
  onSubmit,
  onConfirm,
  onBack,
  onClose,
  onClearError
}) => {
  const { t: translate } = useTranslation(I18nNamespace.Personas);

  const title = React.useMemo(() => {
    switch (currentStep) {
      case AIRewriteStep.INSTRUCTIONS:
        return translate(nameof.full<I18nPersonasNs>((n) => n.aiRewrite.dialogTitle));
      case AIRewriteStep.LOADING:
        return translate(nameof.full<I18nPersonasNs>((n) => n.aiRewrite.loadingTitle));
      case AIRewriteStep.RESULT:
        return translate(nameof.full<I18nPersonasNs>((n) => n.aiRewrite.resultTitle));
      default:
        return translate(nameof.full<I18nPersonasNs>((n) => n.aiRewrite.dialogTitle));
    }
  }, [currentStep, translate]);

  const allowClose = currentStep !== AIRewriteStep.LOADING;

  return (
    <Dialog
      open={isOpen}
      onClose={allowClose ? onClose : undefined}
      maxWidth="md"
      fullWidth
    >
      <DialogTitle>
        {title}
        {allowClose && (
          <IconButton
            onClick={onClose}
            sx={{ position: 'absolute', right: 8, top: 8 }}
            aria-label={translate(nameof.full<I18nPersonasNs>((n) => n.aiRewrite.closeButton))}
          >
            <Close />
          </IconButton>
        )}
      </DialogTitle>

      {currentStep === AIRewriteStep.INSTRUCTIONS && (
        <>
          <DialogContent>
            <Box sx={{ mb: 2 }}>
              <Typography variant="body1" color="text.secondary">
                {translate(nameof.full<I18nPersonasNs>((n) => n.aiRewrite.instructionsDescription))}
              </Typography>
            </Box>

            {error && (
              <Box
                role="alert"
                aria-live="assertive"
                sx={{
                  mb: 2,
                  p: 1.5,
                  borderRadius: 1,
                  bgcolor: 'error.light',
                  color: 'error.contrastText',
                  cursor: 'pointer'
                }}
                onClick={onClearError}
              >
                <Typography variant="body2">{error}</Typography>
              </Box>
            )}

            <TextField
              fullWidth
              multiline
              minRows={4}
              label={translate(nameof.full<I18nPersonasNs>((n) => n.aiRewrite.customInstructionsLabel))}
              placeholder={translate(nameof.full<I18nPersonasNs>((n) => n.aiRewrite.customInstructionsPlaceholder))}
              value={customInstructions}
              onChange={(event) => onInstructionsChange(event.target.value)}
              helperText={translate(nameof.full<I18nPersonasNs>((n) => n.aiRewrite.customInstructionsHelperText))}
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={onClose} disabled={isLoading}>
              {translate(nameof.full<I18nPersonasNs>((n) => n.aiRewrite.cancelButton))}
            </Button>
            <Button
              onClick={onSubmit}
              variant="contained"
              disabled={isLoading}
              startIcon={<AutoAwesome />}
            >
              {translate(nameof.full<I18nPersonasNs>((n) => n.aiRewrite.rewriteButton))}
            </Button>
          </DialogActions>
        </>
      )}

      {currentStep === AIRewriteStep.LOADING && (
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', py: 4 }}>
            <CircularProgress sx={{ mb: 2 }} />
            <Typography variant="body1">
              {translate(nameof.full<I18nPersonasNs>((n) => n.aiRewrite.loadingMessage))}
            </Typography>
          </Box>
        </DialogContent>
      )}

      {currentStep === AIRewriteStep.RESULT && rewrittenDescription && (
        <>
          <DialogContent>
            <Typography variant="body1" color="text.secondary" sx={{ mb: 2 }}>
              {translate(nameof.full<I18nPersonasNs>((n) => n.aiRewrite.resultDescription))}
            </Typography>

            <Box sx={{ mb: 3 }}>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>
                {translate(nameof.full<I18nPersonasNs>((n) => n.aiRewrite.originalText))}
              </Typography>
              <Paper variant="outlined" sx={{ p: 2, bgcolor: 'grey.50' }}>
                <Typography variant="body2" color="text.secondary">
                  {originalDescription?.trim()
                    ? originalDescription
                    : translate(nameof.full<I18nPersonasNs>((n) => n.aiRewrite.emptyDescription))}
                </Typography>
              </Paper>
            </Box>

            <Divider sx={{ my: 2 }} />

            <Box>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>
                {translate(nameof.full<I18nPersonasNs>((n) => n.aiRewrite.rewrittenText))}
              </Typography>
              <Paper variant="outlined" sx={{ p: 2 }}>
                <Typography variant="body2">{rewrittenDescription}</Typography>
              </Paper>
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={onClose}>
              {translate(nameof.full<I18nPersonasNs>((n) => n.aiRewrite.cancelButton))}
            </Button>
            <Button onClick={onBack} startIcon={<ArrowBack />}>
              {translate(nameof.full<I18nPersonasNs>((n) => n.aiRewrite.backButton))}
            </Button>
            <Button onClick={onConfirm} variant="contained" startIcon={<Check />}>
              {translate(nameof.full<I18nPersonasNs>((n) => n.aiRewrite.confirmButton))}
            </Button>
          </DialogActions>
        </>
      )}
    </Dialog>
  );
};

export default AIRewriteDialog;
